import React, { useState, useEffect } from 'react';
import {
  Search, ChevronLeft, ChevronRight, Filter, X, Lock,
  CircleCheck, CircleAlert, CircleX, Banknote, Smartphone, Receipt,
  CreditCard,
} from 'lucide-react';
import { host } from '../script/variables';
import PaymentMethodsChart from './PaymentMethodsChart';
import HoldOrders from './HoldOrders';
import '../style/Transactions.css';

// Icon + tone shown next to each status so meaning never depends on color alone
const STATUS_META = {
  successful: { Icon: CircleCheck, label: 'Successful' },
  pending:    { Icon: CircleAlert, label: 'Pending' },
  failed:     { Icon: CircleX,     label: 'Failed' },
  hold:       { Icon: Lock,        label: 'On Hold' },
  cash:       { Icon: Banknote,    label: 'Cash' },
  online:     { Icon: Smartphone,  label: 'Online' },
};

const TransactionView = ({ cafeid }) => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage]   = useState(1);
  const [pagination, setPagination]     = useState(null);
  const [showFilters, setShowFilters]   = useState(false);
  const [totalAmount, setTotalAmount]   = useState(0);

  // ── date filter — same model as the Bank Settlement tab: pick a period
  // chip, and only reveal raw date inputs when "Custom" is chosen.
  const [period, setPeriod] = useState('today');

  // Shifts to IST before extracting the date string
  const getTodayIST = () =>
    new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(getTodayIST);
  const [endDate, setEndDate]     = useState(getTodayIST);

  useEffect(() => {
    fetchTransactions();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, startDate, endDate]);

  /* ---------- Fetch ---------- */
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/transtats/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          cafeid, page: currentPage, limit: 10,
          status: statusFilter, search: searchTerm, startDate, endDate
        })
      });
      const data = await response.json();
      if (data.success) {
        setTransactions(data.data);
        setPagination(data.pagination);
        setTotalAmount(data.totalAmount || 0);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/transtats/transactions/stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ cafeid, startDate, endDate })
      });
      const data = await response.json();
      if (data.success) setStats(data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  /* ---------- Handlers ---------- */
  const handleSearch = () => { setCurrentPage(1); fetchTransactions(); };
  const handleSearchKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };
  const handleFilterChange = (filter) => { setStatusFilter(filter); setCurrentPage(1); };

  // Every preset resolves through this one function so the "currently active"
  // pill can be found later by comparing state to the same values a click would set.
  const computePresetRange = (preset) => {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const todayIST = new Date(Date.now() + IST_OFFSET_MS).toISOString().split('T')[0];
    const toISTStr = (d) => new Date(d.getTime() + IST_OFFSET_MS).toISOString().split('T')[0];
    const now = new Date();

    switch (preset) {
      case 'today':
        return { start: todayIST, end: todayIST };
      case 'yesterday': {
        const d = new Date(now);
        d.setDate(d.getDate() - 1);
        const s = toISTStr(d);
        return { start: s, end: s };
      }
      case 'thisWeek': {
        const d = new Date(now);
        d.setDate(d.getDate() - d.getDay());
        return { start: toISTStr(d), end: todayIST };
      }
      case 'thisMonth': {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: toISTStr(d), end: todayIST };
      }
      default:
        return null;
    }
  };

  const handlePeriod = (key) => {
    setPeriod(key);
    setCurrentPage(1);
    if (key === 'custom') return; // wait for the user to pick Start/End
    const range = computePresetRange(key);
    if (range) { setStartDate(range.start); setEndDate(range.end); }
  };

  /* ---------- Formatters ---------- */
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency = 'INR') =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount || 0);

  const PERIODS = [
    { key: 'today',     label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek',  label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'custom',    label: 'Custom' },
  ];

  const FILTER_CHIPS = [
    { value: 'all',        label: 'All Payments',   Icon: Receipt },
    { value: 'successful', label: 'Successful',     Icon: CircleCheck },
    { value: 'pending',    label: 'Pending',        Icon: CircleAlert },
    { value: 'failed',     label: 'Failed',         Icon: CircleX },
    { value: 'hold',       label: 'On Hold',        Icon: Lock },
    { value: 'cash',       label: 'Cash Payments',  Icon: Banknote },
    { value: 'online',     label: 'Online Payments', Icon: Smartphone },
  ];

  const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] ?? { Icon: Receipt, label: status };
    return (
      <span className={`txn-status-badge txn-status-badge--${status}`}>
        <meta.Icon size={13} strokeWidth={2.4} />
        {meta.label}
      </span>
    );
  };

  /* ---------- UI ---------- */
  // Everything is scoped under .txn-dashboard so no bare element/class
  // selector here can leak out to — or be overridden by — the parent page.
  return (
    <div className="txn-dashboard">

      {/* ── Header — matches Bank Settlement tab for a consistent look ── */}
      <div className="txn-header">
        <div className="txn-header-eyebrow">
          <CreditCard size={16} strokeWidth={2.2} />
          <span>Payments</span>
        </div>
        <p className="txn-page-subtitle">Every order payment, searchable</p>
      </div>

      {/* ── Date filter — matches the Bank Settlement tab's period chips ── */}
      <div className="txn-period-row" role="group" aria-label="Time period">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => handlePeriod(p.key)}
            className={`txn-period-chip ${period === p.key ? 'txn-period-chip--active' : ''}`}
            aria-pressed={period === p.key}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ── Custom date range — only shown when "Custom" is picked ── */}
      {period === 'custom' && (
        <div className="txn-date-row">
          <div className="txn-date-field">
            <label className="txn-date-label" htmlFor="txn-start-date">Start Date</label>
            <input id="txn-start-date" type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} className="txn-date-input" />
          </div>
          <div className="txn-date-sep">→</div>
          <div className="txn-date-field">
            <label className="txn-date-label" htmlFor="txn-end-date">End Date</label>
            <input id="txn-end-date" type="date" value={endDate} min={startDate} max={getTodayIST()} onChange={e => setEndDate(e.target.value)} className="txn-date-input" />
          </div>
        </div>
      )}

      {/* ── Search + status filters ── */}
      <div className="txn-controls-card">
        <div className="txn-controls-row">
          {/* Search */}
          <div className="txn-search">
            <Search className="txn-search-icon" size={17} />
            <input
              type="text"
              placeholder="Search by order ID or transaction ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="txn-search-input"
            />
            <button type="button" onClick={handleSearch} className="txn-search-btn">Search</button>
          </div>

          {/* Filter toggle */}
          <button type="button" onClick={() => setShowFilters(!showFilters)} className="txn-filter-toggle">
            <Filter size={16} />
            Filters
            {statusFilter !== 'all' && <span className="txn-filter-badge">1</span>}
          </button>
        </div>

        {/* ── Filter chips ── */}
        {showFilters && (
          <div className="txn-filter-chips">
            {FILTER_CHIPS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => handleFilterChange(filter.value)}
                className={`txn-chip ${statusFilter === filter.value ? 'txn-chip--active' : 'txn-chip--inactive'} ${filter.value === 'hold' ? 'txn-chip--hold' : ''}`}
              >
                <filter.Icon size={13} strokeWidth={2.4} />
                {filter.label}
              </button>
            ))}
            {statusFilter !== 'all' && (
              <button type="button" onClick={() => handleFilterChange('all')} className="txn-chip txn-chip--clear">
                <X size={14} /> Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Total amount */}
      {totalAmount > 0 && (
        <div className="txn-total-amount">
          <span className="txn-total-label">Total for "{FILTER_CHIPS.find(f => f.value === statusFilter)?.label ?? 'All'}"</span>
          <span className="txn-total-value">{formatAmount(totalAmount)}</span>
        </div>
      )}

      {/* Payment Methods Chart */}
      <PaymentMethodsChart cafeid={cafeid} startDate={startDate} endDate={endDate} />

      {/* ── Hold Orders Section ── */}
      <HoldOrders cafeid={cafeid} startDate={startDate} endDate={endDate} />

      {/* ── Transactions list ── */}
      <div className="txn-table-card">
        {loading ? (
          <div className="txn-loading">
            <div className="txn-spinner" />
            <p>Loading payments…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="txn-empty">
            <Receipt size={34} strokeWidth={1.6} className="txn-empty-icon" />
            <p className="txn-empty-title">No payments found</p>
            <span className="txn-empty-sub">Try a different date range or clear your filters</span>
          </div>
        ) : (
          <>
            {/* Desktop / tablet table — hidden on small phones */}
            <div className="txn-table-wrap">
              <table className="txn-table">
                <thead>
                  <tr>
                    <th className="txn-th">Transaction ID</th>
                    <th className="txn-th">Amount</th>
                    <th className="txn-th">Status</th>
                    <th className="txn-th">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(txn => (
                    <tr key={txn._id} className={`txn-row ${txn.status === 'hold' ? 'txn-row--hold' : ''}`}>
                      <td className="txn-td">
                        <div className="txn-id">{txn.transaction_id}</div>
                        {txn.status === 'hold' && txn.deliveryStatus && (
                          <div className="txn-hold-delivery">
                            Delivery: <span className={`txn-delivery-tag txn-delivery-tag--${txn.deliveryStatus?.toLowerCase()}`}>{txn.deliveryStatus}</span>
                          </div>
                        )}
                      </td>
                      <td className="txn-td txn-td--amount">{formatAmount(txn.amount, txn.currency)}</td>
                      <td className="txn-td"><StatusBadge status={txn.status} /></td>
                      <td className="txn-td txn-td--date">{formatDate(txn.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — same data, easier to scan with one thumb */}
            <div className="txn-card-list">
              {transactions.map(txn => (
                <div key={txn._id} className={`txn-mini-card ${txn.status === 'hold' ? 'txn-mini-card--hold' : ''}`}>
                  <div className="txn-mini-top">
                    <span className="txn-id">{txn.transaction_id}</span>
                    <span className="txn-mini-amount">{formatAmount(txn.amount, txn.currency)}</span>
                  </div>
                  <div className="txn-mini-bottom">
                    <StatusBadge status={txn.status} />
                    <span className="txn-mini-date">{formatDate(txn.created_at)}</span>
                  </div>
                  {txn.status === 'hold' && txn.deliveryStatus && (
                    <div className="txn-hold-delivery">
                      Delivery: <span className={`txn-delivery-tag txn-delivery-tag--${txn.deliveryStatus?.toLowerCase()}`}>{txn.deliveryStatus}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="txn-pagination">
                <div className="txn-pagination-mobile">
                  <button type="button" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="txn-page-nav-btn">
                    <ChevronLeft size={18} /> Previous
                  </button>
                  <button type="button" onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))} disabled={currentPage === pagination.totalPages} className="txn-page-nav-btn">
                    Next <ChevronRight size={18} />
                  </button>
                </div>
                <div className="txn-pagination-desktop">
                  <span className="txn-pagination-info">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                  </span>
                  <div className="txn-pagination-controls">
                    <button type="button" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="txn-page-arrow">
                      <ChevronLeft size={16} />
                    </button>
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, idx) => {
                      const pageNum = currentPage <= 3 ? idx + 1 : currentPage - 2 + idx;
                      if (pageNum > pagination.totalPages) return null;
                      return (
                        <button key={pageNum} type="button" onClick={() => setCurrentPage(pageNum)}
                          className={`txn-page-number ${currentPage === pageNum ? 'txn-page-number--active' : ''}`}>
                          {pageNum}
                        </button>
                      );
                    })}
                    <button type="button" onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))} disabled={currentPage === pagination.totalPages} className="txn-page-arrow">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionView;